import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  writeBatch,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import { db } from "../../config/firebase";
import { useGoogleMapsDistance } from "../../hooks/useGoogleMapsDistance";
import { apiMapsTolls } from "../../services/mapsConfigApi";
import { apiEnviarPropostaEmail } from "../../services/financeiroPropostasEmailApi";
import { blobToBase64, gerarPdfInstalacao } from "../../services/financeiroPropostasPdfService";

const COLLECTION_PROPOSTAS_INSTALACAO = "financeiro_propostas_instalacoes";
const COLLECTION_VALORES_SERVICOS = "financeiro_valores_servicos";
const COLLECTION_SESSOES = "atendimentos_sessoes";
const COLLECTION_ATENDIMENTOS = "atendimentos_preventiva";
const COLLECTION_ITENS = "atendimentos_sessao_itens";
const COLLECTION_CHECKLIST_MODELOS = "checklistsModelos";
const STATUS_OPTIONS = ["CRIADA", "ENVIADA", "APROVADA", "REPROVADA", "FINALIZADA"];
const TIPOS_EQUIPAMENTO = [
  "Pin Load",
  "Plate Load",
  "Banco",
  "Cross",
  "Guiado",
  "Livre",
  "Esteira",
  "Bike",
  "Spinning",
  "Eliptico",
  "Escada",
  "Remo",
];

function parseNumero(value) {
  let raw = String(value || "").trim();
  if (!raw) return 0;
  
  // Se tem separador de milhares (ex: 1.200,50), remove o ponto e converte vírgula
  if (raw.includes(".") && raw.includes(",")) {
    raw = raw.replace(".", "").replace(",", ".");
  }
  // Se só tem vírgula (ex: 1200,50), converte para ponto
  else if (raw.includes(",")) {
    raw = raw.replace(",", ".");
  }
  // Se tem ponto mas é formato decimal (ex: 97.5 ou 1.2), mantém como está
  // (não remove o ponto) porque é já decimal
  
  const num = Number.parseFloat(raw);
  return Number.isNaN(num) ? 0 : num;
}

function formatNumero(value, decimals = 2) {
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return num.toFixed(decimals);
}

function buildDefaultValoresPorTipo() {
  const init = {};
  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    init[tipo] = 0;
  });
  return init;
}

function montarEndereco(data) {
  if (!data) return "";
  const parts = [
    data.enderecoCompleto,
    data.endereco,
    [data.logradouro, data.numero].filter(Boolean).join(", "),
    data.bairro,
    data.cidade,
    data.estado,
    data.cep,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  return Array.from(new Set(parts)).join(" - ");
}

function isContratoAvulso(value) {
  return String(value || "").toUpperCase().includes("AVULSO");
}

function normalizarStatusVisita(sessao) {
  const raw = String(sessao?.status || "").trim().toUpperCase();
  if (raw === "FINALIZADA") return "FINALIZADA";
  if (raw === "CANCELADA") return "CANCELADA";
  if (raw === "ATENDIDA" || raw === "REALIZADA") return "ATENDIDA";
  if (raw === "AGENDADA") return "AGENDADA";
  return "PLANEJADA";
}

function normalizarMapaEquipamentos(input) {
  const base = {};
  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    const qtd = Number(input?.[tipo] || 0);
    base[tipo] = Number.isFinite(qtd) && qtd > 0 ? Math.floor(qtd) : 0;
  });
  return base;
}

async function reservarSequenciaisEquipamentos(grupoId, quantidade) {
  if (!grupoId || quantidade <= 0) return { inicio: 0, fim: 0 };

  const contadorRef = doc(db, "contadores", `equipamentos_${grupoId}`);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(contadorRef);
    const atual = snap.exists() ? Number(snap.data()?.seq || 0) : 0;
    const inicio = atual + 1;
    const fim = atual + quantidade;

    tx.set(
      contadorRef,
      {
        grupoId,
        seq: fim,
        atualizadoEm: serverTimestamp(),
      },
      { merge: true }
    );

    return { inicio, fim };
  });
}

async function gerarEquipamentosDaPropostaAprovada({
  propostaId,
  propostaData,
  usuario,
  academiaFallback,
}) {
  const grupoId = propostaData?.grupoId || usuario?.grupoId || "";
  const clienteId = propostaData?.clienteId || null;
  const clienteNome = propostaData?.clienteNome || "";
  const academiaId = propostaData?.academiaId || null;

  if (!grupoId || !propostaId || !clienteId || !academiaId) {
    return { criados: 0, vinculadosTotal: 0 };
  }

  const mapaObjetivo = normalizarMapaEquipamentos(propostaData?.equipamentosPorTipo || {});
  const objetivoTotal = Object.values(mapaObjetivo).reduce((acc, qtd) => acc + Number(qtd || 0), 0);
  if (objetivoTotal <= 0) {
    return { criados: 0, vinculadosTotal: 0 };
  }

  const snapExistentes = await getDocs(
    query(collection(db, "maquinas"), where("propostaInstalacaoId", "==", propostaId))
  );

  const existentes = snapExistentes.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  const existentesPorTipo = {};
  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    existentesPorTipo[tipo] = 0;
  });

  existentes.forEach((m) => {
    const tipo = String(m?.tipo || "").trim();
    if (!tipo) return;
    if (existentesPorTipo[tipo] == null) existentesPorTipo[tipo] = 0;
    existentesPorTipo[tipo] += 1;
  });

  const deficitsPorTipo = {};
  let totalCriar = 0;
  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    const alvo = Number(mapaObjetivo[tipo] || 0);
    const existente = Number(existentesPorTipo[tipo] || 0);
    const falta = Math.max(0, alvo - existente);
    deficitsPorTipo[tipo] = falta;
    totalCriar += falta;
  });

  const vinculadosTotal = existentes.length + totalCriar;
  if (totalCriar <= 0) {
    return { criados: 0, vinculadosTotal };
  }

  let academiaData = academiaFallback || null;
  if (!academiaData || String(academiaData?.id || "") !== String(academiaId)) {
    const academiaSnap = await getDoc(doc(db, "setores", academiaId));
    academiaData = academiaSnap.exists() ? { id: academiaSnap.id, ...academiaSnap.data() } : null;
  }

  const contratoTipoRaw = String(
    academiaData?.tipoContrato || academiaData?.tipo || propostaData?.setorContratoTipo || "Avulso"
  ).trim();
  const setorContratoTipo = contratoTipoRaw || "Avulso";

  const { inicio } = await reservarSequenciaisEquipamentos(grupoId, totalCriar);

  const batch = writeBatch(db);
  let cursor = inicio;

  TIPOS_EQUIPAMENTO.forEach((tipo) => {
    const faltantes = Number(deficitsPorTipo[tipo] || 0);
    if (faltantes <= 0) return;

    const jaExistentesDoTipo = Number(existentesPorTipo[tipo] || 0);
    for (let i = 0; i < faltantes; i += 1) {
      const ordemTipo = jaExistentesDoTipo + i + 1;
      const codigoSequencial = cursor;
      const codigo = `EQ${String(codigoSequencial).padStart(4, "0")}`;
      cursor += 1;

      const maquinaRef = doc(collection(db, "maquinas"));
      batch.set(maquinaRef, {
        codigoSequencial,
        codigo,
        nome: `${tipo} ${String(ordemTipo).padStart(2, "0")}`,
        tipo,
        marca: "",
        modelo: "",
        numeroSerie: "",
        cor: "",
        instaladoEm: "",
        montadoPor: "",
        condicoesIniciais: 3,
        descricao: "",
        fotosUrls: [],
        fotoUrl: "",
        grupoId,
        empresaId: clienteId,
        empresaNome: clienteNome || "",
        setorId: academiaId,
        setorNome: academiaData?.nome || propostaData?.academiaNome || "",
        setorCodigo: academiaData?.codigo || "",
        setorContratoTipo,
        preventivasModeloIds: [],
        checklistInstalacaoAtiva: false,
        checklistInstalacaoId: null,
        checklistInstalacaoNome: "",
        propostaInstalacaoId: propostaId,
        propostaInstalacaoOrigem: true,
        criadoEm: serverTimestamp(),
        criadoPor: usuario?.uid || usuario?.id || "",
        atualizadoEm: serverTimestamp(),
      });
    }
  });

  await batch.commit();
  return { criados: totalCriar, vinculadosTotal };
}

export default function FinanceiroInstalacaoFormPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [clientes, setClientes] = useState([]);
  const [academias, setAcademias] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [visitas, setVisitas] = useState([]);

  const [visitaId, setVisitaId] = useState("");
  const [atendimentoId, setAtendimentoId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [academiaId, setAcademiaId] = useState("");
  const [status, setStatus] = useState("CRIADA");

  const [observacoes, setObservacoes] = useState("");
  const [enderecoDestino, setEnderecoDestino] = useState("");

  const [kmRodados, setKmRodados] = useState("");
  const [kmIda, setKmIda] = useState(0);
  const [kmVolta, setKmVolta] = useState(0);
  const [valorDeslocamento, setValorDeslocamento] = useState("");
  const [valorPedagio, setValorPedagio] = useState("");
  const [pedagioIda, setPedagioIda] = useState(0);
  const [pedagioVolta, setPedagioVolta] = useState(0);
  const [valorDesconto, setValorDesconto] = useState("");

  const [tipoViagem, setTipoViagem] = useState("CARRO");
  const [quantidadeAjudantes, setQuantidadeAjudantes] = useState("");
  const [gastosAlmoco, setGastosAlmoco] = useState("");
  const [previsaoDias, setPrevisaoDias] = useState("");

  const [enderecoOrigem, setEnderecoOrigem] = useState("");
  const [valorKmDeslocamento, setValorKmDeslocamento] = useState(1.2);
  const [idaVolta, setIdaVolta] = useState(true);

  const [quantEquipPorTipo, setQuantEquipPorTipo] = useState(() => {
    const init = {};
    TIPOS_EQUIPAMENTO.forEach((tipo) => {
      init[tipo] = 0;
    });
    return init;
  });
  const [valoresInstalacaoPorTipo, setValoresInstalacaoPorTipo] = useState(() => buildDefaultValoresPorTipo());

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [carregandoRegistro, setCarregandoRegistro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [calculandoDistancia, setCalculandoDistancia] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [carregandoChecklists, setCarregandoChecklists] = useState(false);
  const [salvandoEntradaRapida, setSalvandoEntradaRapida] = useState(false);

  const [checklistModelos, setChecklistModelos] = useState([]);
  const [edicaoMaquinas, setEdicaoMaquinas] = useState({});
  const [selecionadasMaquinas, setSelecionadasMaquinas] = useState({});

  const [loteMarca, setLoteMarca] = useState("");
  const [loteModelo, setLoteModelo] = useState("");
  const [loteChecklistId, setLoteChecklistId] = useState("");
  const [loteSeriaisTexto, setLoteSeriaisTexto] = useState("");

  const isEdicao = !!id;
  const grupoId = usuario?.grupoId || "";

  const { calcularDistancia, gerarUrlGoogleMaps } = useGoogleMapsDistance();

  const clienteSelecionado = useMemo(
    () => clientes.find((c) => String(c.id) === String(clienteId)) || null,
    [clientes, clienteId]
  );

  const academiaSelecionada = useMemo(
    () => academias.find((a) => String(a.id) === String(academiaId)) || null,
    [academias, academiaId]
  );

  const visitaSelecionada = useMemo(
    () => visitas.find((v) => String(v.id) === String(visitaId)) || null,
    [visitas, visitaId]
  );

  const maquinasFiltradas = useMemo(() => {
    const base = maquinas.filter((m) => String(m.empresaId || "") === String(clienteId || ""));
    if (!academiaId) return base;
    return base.filter((m) => String(m.setorId || "") === String(academiaId));
  }, [maquinas, clienteId, academiaId]);

  const maquinasVinculadasProposta = useMemo(() => {
    if (!isEdicao || !id) return [];
    return maquinas
      .filter((m) => String(m?.propostaInstalacaoId || "") === String(id))
      .sort((a, b) => {
        const seqA = Number(a?.codigoSequencial || 0);
        const seqB = Number(b?.codigoSequencial || 0);
        if (seqA !== seqB) return seqA - seqB;
        return String(a?.codigo || "").localeCompare(String(b?.codigo || ""), "pt-BR", {
          sensitivity: "base",
        });
      });
  }, [maquinas, isEdicao, id]);

  const checklistNomeById = useMemo(() => {
    const map = new Map();
    checklistModelos.forEach((m) => {
      map.set(String(m.id), String(m.nome || m.codigo || m.id));
    });
    return map;
  }, [checklistModelos]);

  const idsSelecionados = useMemo(
    () => maquinasVinculadasProposta.filter((maq) => !!selecionadasMaquinas[maq.id]).map((maq) => maq.id),
    [maquinasVinculadasProposta, selecionadasMaquinas]
  );

  const totalSelecionadas = idsSelecionados.length;
  const allSelecionadas = maquinasVinculadasProposta.length > 0 && totalSelecionadas === maquinasVinculadasProposta.length;

  const totalEquipamentos = useMemo(
    () => Object.values(quantEquipPorTipo).reduce((acc, qtd) => acc + Number(qtd || 0), 0),
    [quantEquipPorTipo]
  );

  const valorEquipamentos = useMemo(
    () => TIPOS_EQUIPAMENTO.reduce(
      (acc, tipo) => acc + (Number(quantEquipPorTipo[tipo] || 0) * Number(valoresInstalacaoPorTipo[tipo] || 0)),
      0
    ),
    [quantEquipPorTipo, valoresInstalacaoPorTipo]
  );

  const valorTotal = useMemo(() => {
    const desloc = parseNumero(valorDeslocamento);
    const pedagio = parseNumero(valorPedagio);
    const almoco = parseNumero(gastosAlmoco);
    const desconto = parseNumero(valorDesconto);
    return desloc + pedagio + almoco + valorEquipamentos - desconto;
  }, [valorDeslocamento, valorPedagio, gastosAlmoco, valorDesconto, valorEquipamentos]);

  useEffect(() => {
    const carregarChecklists = async () => {
      if (!grupoId) {
        setChecklistModelos([]);
        return;
      }

      setCarregandoChecklists(true);
      try {
        const snap = await getDocs(
          query(collection(db, COLLECTION_CHECKLIST_MODELOS), where("grupoId", "==", grupoId))
        );

        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || a.codigo || "").localeCompare(String(b.nome || b.codigo || ""), "pt-BR"));

        setChecklistModelos(lista);
      } catch (e) {
        console.error("Erro ao carregar modelos de checklist:", e);
        setChecklistModelos([]);
      } finally {
        setCarregandoChecklists(false);
      }
    };

    carregarChecklists();
  }, [grupoId]);

  useEffect(() => {
    if (!isEdicao || !id) {
      setEdicaoMaquinas({});
      setSelecionadasMaquinas({});
      return;
    }

    const next = {};
    maquinasVinculadasProposta.forEach((maq) => {
      next[maq.id] = {
        marca: String(maq?.marca || ""),
        modelo: String(maq?.modelo || ""),
        numeroSerie: String(maq?.numeroSerie || ""),
        checklistInstalacaoId: String(maq?.checklistInstalacaoId || ""),
        checklistInstalacaoNome: String(maq?.checklistInstalacaoNome || ""),
      };
    });
    setEdicaoMaquinas(next);
  }, [isEdicao, id, maquinasVinculadasProposta]);

  useEffect(() => {
    if (!isEdicao || !id) {
      setSelecionadasMaquinas({});
      return;
    }

    setSelecionadasMaquinas((prev) => {
      const next = {};
      maquinasVinculadasProposta.forEach((maq) => {
        next[maq.id] = prev[maq.id] ?? true;
      });
      return next;
    });
  }, [isEdicao, id, maquinasVinculadasProposta]);

  useEffect(() => {
    const carregarValores = async () => {
      if (!usuario?.grupoId) return;
      try {
        const snap = await getDoc(doc(db, COLLECTION_VALORES_SERVICOS, usuario.grupoId));
        if (!snap.exists()) return;
        const data = snap.data() || {};
        if (data.instalacaoPorTipo && typeof data.instalacaoPorTipo === "object") {
          setValoresInstalacaoPorTipo((prev) => ({ ...prev, ...data.instalacaoPorTipo }));
        }
      } catch (e) {
        console.warn("Erro ao carregar valores de instalação por tipo:", e);
      }
    };

    carregarValores();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarBase = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      try {
        const [snapClientes, snapMaquinas] = await Promise.all([
          getDocs(query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId))),
          getDocs(query(collection(db, "maquinas"), where("grupoId", "==", usuario.grupoId))),
        ]);

        setClientes(
          snapClientes.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
        );
        setMaquinas(snapMaquinas.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Erro ao carregar base de instalação:", e);
        setErro("Erro ao carregar clientes/equipamentos.");
      } finally {
        setCarregando(false);
      }
    };

    carregarBase();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarEnderecoGrupo = async () => {
      if (!usuario?.grupoId) return;
      try {
        const grupoRef = doc(db, "grupos", usuario.grupoId);
        const grupoSnap = await getDoc(grupoRef);
        if (!grupoSnap.exists()) return;

        const grupoData = grupoSnap.data() || {};
        setEnderecoOrigem(grupoData.enderecoOrigem || "");
        console.log("[Instalacao] Dados do grupo carregados:", grupoData);
        
        if (grupoData.valorKmDeslocamento != null) {
          const parsed = Number.parseFloat(String(grupoData.valorKmDeslocamento));
          if (!Number.isNaN(parsed)) {
            setValorKmDeslocamento(parsed);
            console.log("[Instalacao] Valor por KM carregado:", parsed);
          }
        } else {
          console.warn("[Instalacao] valorKmDeslocamento não encontrado nos dados do grupo");
        }
      } catch (e) {
        console.error("Erro ao carregar endereço do grupo:", e);
      }
    };

    carregarEnderecoGrupo();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarVisitas = async () => {
      if (!usuario?.grupoId) return;

      try {
        const snapSessoes = await getDocs(
          query(
            collection(db, COLLECTION_SESSOES),
            where("grupoId", "==", usuario.grupoId)
          )
        );

        const sessoes = snapSessoes.docs.map((d) => ({ id: d.id, ...d.data() }));
        const atendimentoIds = Array.from(
          new Set(sessoes.map((s) => String(s.atendimentoId || "")).filter(Boolean))
        );

        const atendimentos = await Promise.all(
          atendimentoIds.map(async (atId) => {
            try {
              const snap = await getDoc(doc(db, COLLECTION_ATENDIMENTOS, atId));
              if (snap.exists()) return { id: snap.id, ...snap.data() };
            } catch {
              return null;
            }
            return null;
          })
        );

        const atendimentosById = new Map(atendimentos.filter(Boolean).map((a) => [String(a.id), a]));

        const itensPorSessao = new Map();
        await Promise.all(
          sessoes.map(async (s) => {
            try {
              const snapItens = await getDocs(
                query(collection(db, COLLECTION_ITENS), where("sessaoId", "==", s.id))
              );
              itensPorSessao.set(s.id, snapItens.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch {
              itensPorSessao.set(s.id, []);
            }
          })
        );

        const filtradas = sessoes
          .filter((s) => {
            const atendimento = atendimentosById.get(String(s.atendimentoId || ""));
            if (!atendimento) return false;

            if (s.propostaId && (!isEdicao || String(s.propostaId) !== String(id))) return false;

            const status = normalizarStatusVisita(s);
            if (status !== "PLANEJADA") return false;

            const itens = itensPorSessao.get(s.id) || [];
            return itens.some((item) => String(item?.tipo || "").toUpperCase() === "INSTALACAO");
          })
          .map((s) => {
            const atendimento = atendimentosById.get(String(s.atendimentoId || "")) || {};
            return {
              ...s,
              atendimento,
              clienteId: atendimento.clienteId || s.clienteId || null,
              academiaId: atendimento.academiaId || atendimento.setorId || s.setorId || null,
            };
          });

        setVisitas(filtradas);
      } catch (e) {
        console.error("Erro ao carregar visitas de instalação:", e);
        setVisitas([]);
      }
    };

    carregarVisitas();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarAcademias = async () => {
      if (!usuario?.grupoId || !clienteId) {
        setAcademias([]);
        setAcademiaId("");
        return;
      }

      try {
        const snap = await getDocs(
          query(
            collection(db, "setores"),
            where("grupoId", "==", usuario.grupoId),
            where("empresaId", "==", clienteId)
          )
        );

        setAcademias(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
        );
      } catch (e) {
        console.error("Erro ao carregar academias:", e);
        setAcademias([]);
      }
    };

    carregarAcademias();
  }, [usuario?.grupoId, clienteId]);

  useEffect(() => {
    if (!isEdicao || !id) return;

    const carregarRegistro = async () => {
      setCarregandoRegistro(true);
      try {
        const snap = await getDoc(doc(db, COLLECTION_PROPOSTAS_INSTALACAO, id));
        if (!snap.exists()) {
          setErro("Proposta não encontrada.");
          return;
        }
        const data = snap.data() || {};
        setVisitaId(data.sessaoId || "");
        setAtendimentoId(data.atendimentoId || "");
        setClienteId(data.clienteId || "");
        setAcademiaId(data.academiaId || "");
        setStatus(data.status || "CRIADA");
        setObservacoes(data.observacoes || "");
        setEnderecoDestino(data.enderecoDestino || "");

        setKmRodados(String(data.kmRodados ?? ""));
        setValorDeslocamento(String(data.valorDeslocamento ?? ""));
        setValorPedagio(String(data.valorPedagio ?? ""));
        setValorDesconto(String(data.valorDesconto ?? ""));

        setTipoViagem(data.tipoViagem || "CARRO");
        setQuantidadeAjudantes(String(data.quantidadeAjudantes ?? ""));
        setGastosAlmoco(String(data.gastosAlmoco ?? ""));
        setPrevisaoDias(String(data.previsaoDias ?? ""));

        // Carregar quantidades editáveis de equipamentos
        if (data.equipamentosPorTipo && typeof data.equipamentosPorTipo === 'object') {
          setQuantEquipPorTipo(prev => ({ ...prev, ...data.equipamentosPorTipo }));
        }
      } catch (e) {
        console.error("Erro ao carregar proposta:", e);
        setErro("Erro ao carregar proposta.");
      } finally {
        setCarregandoRegistro(false);
      }
    };

    carregarRegistro();
  }, [id, isEdicao]);

  useEffect(() => {
    if (!visitaSelecionada) return;
    setAtendimentoId(visitaSelecionada.atendimentoId || "");
    setClienteId(visitaSelecionada.clienteId || "");
    setAcademiaId(visitaSelecionada.academiaId || "");
  }, [visitaSelecionada]);

  useEffect(() => {
    const totalKm = idaVolta ? Number(kmIda || 0) + Number(kmVolta || 0) : Number(kmIda || 0);
    if (totalKm > 0) setKmRodados(totalKm.toString());
  }, [idaVolta, kmIda, kmVolta]);

  useEffect(() => {
    const totalPedagio = idaVolta ? Number(pedagioIda || 0) + Number(pedagioVolta || 0) : Number(pedagioIda || 0);
    setValorPedagio(formatNumero(totalPedagio));
  }, [idaVolta, pedagioIda, pedagioVolta]);

  useEffect(() => {
    if (tipoViagem === "AVIAO") {
      console.log("[Instalacao] Tipo de viagem é AVIÃO, não atualizando deslocamento");
      return;
    }
    // Use Number() diretamente para input type="number"
    const km = Number(kmRodados || 0);
    const valor = formatNumero(km * valorKmDeslocamento);
    console.log(`[Instalacao] Calculando deslocamento: ${km} km × R$ ${valorKmDeslocamento} = R$ ${valor}`);
    setValorDeslocamento(valor);
  }, [kmRodados, tipoViagem, valorKmDeslocamento]);

  useEffect(() => {
    const enderecoAcademia = montarEndereco(academiaSelecionada);
    const enderecoCliente = montarEndereco(clienteSelecionado);
    if (!isEdicao) setEnderecoDestino(enderecoAcademia || enderecoCliente || "");
  }, [academiaSelecionada, clienteSelecionado, isEdicao]);

  const handleCalcularDistancia = async () => {
    console.log("[Instalacao] Botão calcular clicado. Validando...");
    console.log("[Instalacao] enderecoOrigem:", enderecoOrigem);
    console.log("[Instalacao] enderecoDestino:", enderecoDestino);
    console.log("[Instalacao] grupoId:", grupoId);
    
    if (!enderecoOrigem) {
      const msg = "Endereço de origem não configurado. Configure em Opções > Grupo.";
      console.log("[Instalacao]", msg);
      setErro(msg);
      return;
    }

    if (!enderecoDestino || enderecoDestino.trim().length < 10) {
      const msg = "Informe o endereço de destino completo (com número, cidade e estado).";
      console.log("[Instalacao]", msg);
      setErro(msg);
      return;
    }

    if (!enderecoOrigem || enderecoOrigem.trim().length < 10) {
      const msg = "Endereço de origem incompleto. Configure endereço completo em Opções > Grupo.";
      console.log("[Instalacao]", msg);
      setErro(msg);
      return;
    }

    setCalculandoDistancia(true);
    setErro("");
    setSucesso("");
    console.log("[Instalacao] Iniciando cálculo de distância...");

    try {
      console.log("[Instalacao] Calculando IDA:", enderecoOrigem, "->", enderecoDestino);
      const resultadoIda = await calcularDistancia(grupoId, enderecoOrigem, enderecoDestino);
      console.log("[Instalacao] Resultado IDA:", resultadoIda);
      
      const novoKmIda = Number(resultadoIda.distanceKm || 0);
      let novoKmVolta = 0;
      let totalKm = novoKmIda;

      if (idaVolta) {
        console.log("[Instalacao] Calculando VOLTA:", enderecoDestino, "->", enderecoOrigem);
        const resultadoVolta = await calcularDistancia(grupoId, enderecoDestino, enderecoOrigem);
        console.log("[Instalacao] Resultado VOLTA:", resultadoVolta);
        novoKmVolta = Number(resultadoVolta.distanceKm || 0);
        totalKm += novoKmVolta;
      }

      setKmIda(novoKmIda);
      setKmVolta(novoKmVolta);
      setKmRodados(String(totalKm));
      const sucessoMsg = idaVolta ? `Ida: ${novoKmIda} km | Volta: ${novoKmVolta} km | Total: ${totalKm} km` : `Distância: ${novoKmIda} km`;
      setSucesso(sucessoMsg);
      console.log("[Instalacao] Sucesso distância:", sucessoMsg);

      try {
        // Pedágio da ida
        console.log("[Instalacao] Calculando pedágio IDA...");
        const tollIda = await apiMapsTolls({ grupoId, origin: enderecoOrigem, destination: enderecoDestino });
        const valorIda = Number(tollIda?.tollValue || 0);
        setPedagioIda(valorIda);
        console.log(`[Instalacao] Pedágio Ida: R$ ${valorIda}`);

        if (idaVolta) {
          console.log("[Instalacao] Calculando pedágio VOLTA...");
          const tollVolta = await apiMapsTolls({ grupoId, origin: enderecoDestino, destination: enderecoOrigem });
          const valorVolta = Number(tollVolta?.tollValue || 0);
          setPedagioVolta(valorVolta);
          console.log(`[Instalacao] Pedágio Volta: R$ ${valorVolta}`);
          setSucesso(prev => prev + ` | Pedágios: R$ ${(valorIda + valorVolta).toFixed(2)}`);
        } else {
          setPedagioVolta(0);
          console.log(`[Instalacao] Sem pedágio volta (apenas ida)`);
          setSucesso(prev => prev + ` | Pedágio: R$ ${valorIda.toFixed(2)}`);
        }
      } catch (tollErr) {
        console.error("[Instalacao] Erro ao calcular pedágio:", tollErr);
        setErro("⚠️ Distância calculada, mas houve erro ao calcular pedágios: " + (tollErr.message || "Desconhecido"));
      }
    } catch (e) {
      console.error("[Instalacao] Erro ao calcular distância:", e);
      
      // Tratamento específico de erros da API
      if (e.message === "ZERO_RESULTS") {
        setErro("❌ Nenhuma rota encontrada entre os endereços. Verifique se os endereços estão corretos e completos (com número, cidade e estado).");
      } else if (e.message.includes("NOT_FOUND")) {
        setErro("❌ Um ou ambos os endereços não foram encontrados. Verifique a ortografia e completude dos endereços.");
      } else if (e.message.includes("INVALID_REQUEST")) {
        setErro("❌ Endereços inválidos. Certifique-se de que incluem: logradouro, número, cidade e estado.");
      } else {
        setErro(e.message || "Erro ao calcular distância");
      }
    } finally {
      setCalculandoDistancia(false);
      console.log("[Instalacao] Cálculo finalizado");
    }
  };

  const handleSalvar = async (event) => {
    event.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuario?.grupoId) {
      setErro("Usuario sem grupoId. Faca login novamente.");
      return;
    }

    if (!clienteId) {
      setErro("Selecione o cliente.");
      return;
    }

    if (!academiaId) {
      setErro("Selecione a academia.");
      return;
    }

    if (!previsaoDias || parseNumero(previsaoDias) <= 0) {
      setErro("Informe a previsão de dias.");
      return;
    }

    setSalvando(true);
    try {
      const statusNormalizado = String(status || "").trim().toUpperCase();
      const payload = {
        grupoId: usuario.grupoId,
        sessaoId: visitaId || null,
        atendimentoId: atendimentoId || null,
        clienteId,
        clienteNome: clienteSelecionado?.nome || clienteSelecionado?.razaoSocial || "",
        academiaId,
        academiaNome: academiaSelecionada?.nome || academiaSelecionada?.codigo || "",
        enderecoDestino: enderecoDestino || null,

        observacoes: observacoes.trim() || null,
        status,

        equipamentosTotal: totalEquipamentos,
        equipamentosPorTipo: quantEquipPorTipo,

        tipoViagem,
        quantidadeAjudantes: parseNumero(quantidadeAjudantes) || 0,
        gastosAlmoco: parseNumero(gastosAlmoco) || 0,
        previsaoDias: parseNumero(previsaoDias) || 0,

        kmRodados: parseNumero(kmRodados) || 0,
        valorDeslocamento: parseNumero(valorDeslocamento) || 0,
        valorPedagio: parseNumero(valorPedagio) || 0,
        valorDesconto: parseNumero(valorDesconto) || 0,
        valorEquipamentos,
        valoresInstalacaoPorTipo,
        valorTotal,

        criadoPor: usuario?.uid || usuario?.id || "",
        updatedAt: serverTimestamp(),
      };

      let propostaIdSalva = id || "";
      let resumoGeracao = { criados: 0, vinculadosTotal: 0 };

      if (isEdicao) {
        const propostaRef = doc(db, COLLECTION_PROPOSTAS_INSTALACAO, id);
        await updateDoc(propostaRef, payload);
        propostaIdSalva = id;
      } else {
        const propostaRef = await addDoc(collection(db, COLLECTION_PROPOSTAS_INSTALACAO), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        propostaIdSalva = propostaRef.id;
      }

      if (statusNormalizado === "APROVADA" && propostaIdSalva) {
        resumoGeracao = await gerarEquipamentosDaPropostaAprovada({
          propostaId: propostaIdSalva,
          propostaData: {
            ...payload,
            id: propostaIdSalva,
          },
          usuario,
          academiaFallback: academiaSelecionada,
        });

        await updateDoc(doc(db, COLLECTION_PROPOSTAS_INSTALACAO, propostaIdSalva), {
          equipamentosGeradosAutomaticamente: true,
          equipamentosGeradosEm: serverTimestamp(),
          equipamentosGeradosUltimoLote: Number(resumoGeracao?.criados || 0),
          equipamentosGeradosTotalVinculados: Number(resumoGeracao?.vinculadosTotal || 0),
          updatedAt: serverTimestamp(),
        });
      }

      const baseMsg = isEdicao
        ? "Proposta de instalação atualizada com sucesso."
        : "Proposta de instalação salva com sucesso.";
      const complementoAprovada = statusNormalizado === "APROVADA"
        ? ` ${Number(resumoGeracao?.criados || 0)} equipamento(s) criado(s) automaticamente.`
        : "";

      setSucesso(`${baseMsg}${complementoAprovada}`.trim());

      setTimeout(() => navigate("/financeiro/instalacoes"), 350);
    } catch (e) {
      console.error("Erro ao salvar proposta de instalação:", e);
      setErro("Erro ao salvar proposta de instalação.");
    } finally {
      setSalvando(false);
    }
  };

  const handleGerarPdf = async () => {
    if (!grupoId) {
      setErro("Usuário sem grupoId para gerar PDF.");
      return;
    }

    setErro("");
    setSucesso("");
    setGerandoPdf(true);
    try {
      const dadosPdf = {
        clienteNome: clienteSelecionado?.nome || clienteSelecionado?.razaoSocial || "",
        academiaNome: academiaSelecionada?.nome || academiaSelecionada?.codigo || "",
        status,
        previsaoDias,
        observacoes,
        equipamentosPorTipo: quantEquipPorTipo,
        valorEquipamentos,
        valorDeslocamento,
        valorPedagio,
        gastosAlmoco,
        valorDesconto,
        valorTotal,
      };

      await gerarPdfInstalacao({
        grupoId,
        dados: dadosPdf,
      });
      setSucesso("PDF da proposta de instalação gerado com sucesso.");
    } catch (e) {
      setErro(String(e?.message || e));
    } finally {
      setGerandoPdf(false);
    }
  };

  const handleEnviarEmail = async () => {
    if (!grupoId) {
      setErro("Usuário sem grupoId para enviar e-mail.");
      return;
    }

    const emailPadrao = String(
      academiaSelecionada?.emailFinanceiro
        || academiaSelecionada?.email
        || clienteSelecionado?.emailFinanceiro
        || clienteSelecionado?.email
        || ""
    ).trim();

    const toRaw = window.prompt("E-mail de destino", emailPadrao);
    if (toRaw == null) return;
    const to = String(toRaw || "").trim();
    if (!to) {
      setErro("Informe um e-mail de destino para enviar a proposta.");
      return;
    }

    const dadosPdf = {
      clienteNome: clienteSelecionado?.nome || clienteSelecionado?.razaoSocial || "",
      academiaNome: academiaSelecionada?.nome || academiaSelecionada?.codigo || "",
      status,
      previsaoDias,
      observacoes,
      equipamentosPorTipo: quantEquipPorTipo,
      valorEquipamentos,
      valorDeslocamento,
      valorPedagio,
      gastosAlmoco,
      valorDesconto,
      valorTotal,
    };

    setErro("");
    setSucesso("");
    setEnviandoEmail(true);
    try {
      const { blob, fileName } = await gerarPdfInstalacao({
        grupoId,
        dados: dadosPdf,
        retornarBlob: true,
      });
      const pdfBase64 = await blobToBase64(blob);

      await apiEnviarPropostaEmail({
        grupoId,
        tipo: "instalacao",
        to,
        subject: `Proposta de Instalação - ${dadosPdf.clienteNome || "Cliente"}`,
        fileName,
        pdfBase64,
        context: {
          clienteNome: dadosPdf.clienteNome,
          academiaNome: dadosPdf.academiaNome,
          tipoDocumento: "Proposta de Instalação",
        },
      });

      setSucesso(`Proposta enviada por e-mail para ${to}.`);
    } catch (e) {
      setErro(String(e?.message || e));
    } finally {
      setEnviandoEmail(false);
    }
  };

  const atualizarMaquinaEdicao = (maquinaId, campo, valor) => {
    setEdicaoMaquinas((prev) => {
      const atual = prev[maquinaId] || {
        marca: "",
        modelo: "",
        numeroSerie: "",
        checklistInstalacaoId: "",
        checklistInstalacaoNome: "",
      };
      return {
        ...prev,
        [maquinaId]: {
          ...atual,
          [campo]: valor,
        },
      };
    });
  };

  const selecionarTodos = () => {
    const next = {};
    maquinasVinculadasProposta.forEach((maq) => {
      next[maq.id] = true;
    });
    setSelecionadasMaquinas(next);
  };

  const limparSelecao = () => {
    const next = {};
    maquinasVinculadasProposta.forEach((maq) => {
      next[maq.id] = false;
    });
    setSelecionadasMaquinas(next);
  };

  const selecionarPendentes = () => {
    const next = {};
    maquinasVinculadasProposta.forEach((maq) => {
      const draft = edicaoMaquinas[maq.id] || {};
      const semMarca = !String(draft?.marca || "").trim();
      const semModelo = !String(draft?.modelo || "").trim();
      const semSerie = !String(draft?.numeroSerie || "").trim();
      const semChecklist = !String(draft?.checklistInstalacaoId || "").trim();
      next[maq.id] = semMarca || semModelo || semSerie || semChecklist;
    });
    setSelecionadasMaquinas(next);
  };

  const alternarSelecao = (maquinaId, checked) => {
    setSelecionadasMaquinas((prev) => ({
      ...prev,
      [maquinaId]: !!checked,
    }));
  };

  const getMaquinasAlvo = ({ somenteSelecionadas = false } = {}) => {
    if (!somenteSelecionadas) return maquinasVinculadasProposta;
    return maquinasVinculadasProposta.filter((maq) => !!selecionadasMaquinas[maq.id]);
  };

  const handleAplicarLoteDados = ({ somenteSelecionadas = false } = {}) => {
    const alvos = getMaquinasAlvo({ somenteSelecionadas });
    if (!alvos.length) {
      setErro("Nenhum equipamento selecionado para aplicar os dados em lote.");
      return;
    }

    setErro("");
    const checklistNome = loteChecklistId ? (checklistNomeById.get(String(loteChecklistId)) || "") : "";

    setEdicaoMaquinas((prev) => {
      const next = { ...prev };
      alvos.forEach((maq) => {
        const atual = next[maq.id] || {
          marca: "",
          modelo: "",
          numeroSerie: "",
          checklistInstalacaoId: "",
          checklistInstalacaoNome: "",
        };

        next[maq.id] = {
          ...atual,
          marca: String(loteMarca || ""),
          modelo: String(loteModelo || ""),
          checklistInstalacaoId: String(loteChecklistId || ""),
          checklistInstalacaoNome: String(checklistNome || ""),
        };
      });
      return next;
    });

    setSucesso(`${alvos.length} equipamento(s) receberam marca/modelo/checklist em lote.`);
  };

  const handleAplicarLoteSeriais = ({ somenteSelecionadas = false } = {}) => {
    const seriais = String(loteSeriaisTexto || "")
      .split(/\r?\n/)
      .map((s) => String(s || "").trim())
      .filter(Boolean);

    if (!seriais.length) {
      setErro("Cole uma lista com um número de série por linha.");
      return;
    }

    const alvos = getMaquinasAlvo({ somenteSelecionadas });
    if (!alvos.length) {
      setErro("Nenhum equipamento selecionado para preencher seriais.");
      return;
    }

    setErro("");
    setEdicaoMaquinas((prev) => {
      const next = { ...prev };
      alvos.forEach((maq, idx) => {
        if (idx >= seriais.length) return;
        const atual = next[maq.id] || {
          marca: "",
          modelo: "",
          numeroSerie: "",
          checklistInstalacaoId: "",
          checklistInstalacaoNome: "",
        };
        next[maq.id] = {
          ...atual,
          numeroSerie: seriais[idx],
        };
      });
      return next;
    });

    setSucesso(`Seriais aplicados em ${Math.min(seriais.length, alvos.length)} equipamento(s).`);
  };

  const handleSalvarEntradaRapida = async ({ somenteSelecionadas = false } = {}) => {
    const alvos = getMaquinasAlvo({ somenteSelecionadas });
    if (!alvos.length) {
      setErro("Nenhum equipamento selecionado para salvar.");
      return;
    }

    setErro("");
    setSucesso("");
    setSalvandoEntradaRapida(true);

    try {
      const atualizacoes = alvos
        .map((maq) => {
          const draft = edicaoMaquinas[maq.id] || {};
          const marca = String(draft?.marca || "").trim();
          const modelo = String(draft?.modelo || "").trim();
          const numeroSerie = String(draft?.numeroSerie || "").trim();
          const checklistInstalacaoId = String(draft?.checklistInstalacaoId || "").trim();
          const checklistInstalacaoNome = checklistInstalacaoId
            ? String(draft?.checklistInstalacaoNome || checklistNomeById.get(checklistInstalacaoId) || "").trim()
            : "";

          const mudou =
            marca !== String(maq?.marca || "") ||
            modelo !== String(maq?.modelo || "") ||
            numeroSerie !== String(maq?.numeroSerie || "") ||
            checklistInstalacaoId !== String(maq?.checklistInstalacaoId || "") ||
            checklistInstalacaoNome !== String(maq?.checklistInstalacaoNome || "");

          if (!mudou) return null;

          return {
            id: maq.id,
            payload: {
              marca,
              modelo,
              numeroSerie,
              checklistInstalacaoId: checklistInstalacaoId || null,
              checklistInstalacaoNome,
              checklistInstalacaoAtiva: !!checklistInstalacaoId,
              atualizadoEm: serverTimestamp(),
            },
          };
        })
        .filter(Boolean);

      if (!atualizacoes.length) {
        setSucesso("Sem alterações para salvar.");
        return;
      }

      const chunkSize = 450;
      for (let i = 0; i < atualizacoes.length; i += chunkSize) {
        const parte = atualizacoes.slice(i, i + chunkSize);
        const batch = writeBatch(db);
        parte.forEach((item) => {
          batch.update(doc(db, "maquinas", item.id), item.payload);
        });
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
      }

      const byId = new Map(atualizacoes.map((u) => [u.id, u.payload]));
      setMaquinas((prev) =>
        prev.map((maq) => {
          const upd = byId.get(maq.id);
          return upd ? { ...maq, ...upd } : maq;
        })
      );

      setSucesso(`${atualizacoes.length} equipamento(s) atualizado(s) em lote.`);
    } catch (e) {
      console.error("Erro ao salvar entrada rápida:", e);
      setErro("Erro ao salvar dados em lote dos equipamentos.");
    } finally {
      setSalvandoEntradaRapida(false);
    }
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo={isEdicao ? "Editar Instalação" : "Nova Instalação"}
      subtitulo="Financeiro / Propostas / Instalações"
    >
      <section className="pc-card pc-form-card">
        <header className="pc-form-header">
          <h3>{isEdicao ? "Editar proposta de instalação" : "Nova proposta de instalação"}</h3>
          <p>Preencha cliente/academia, valide quantidade por tipo e finalize o orçamento da instalação.</p>
        </header>

        {carregandoRegistro ? (
          <p style={{ padding: 10 }}>Carregando proposta...</p>
        ) : (
          <form onSubmit={handleSalvar} className="pc-form">
            {erro && (
              <div style={{ background: "#7f1d1d", color: "#fecaca", padding: 8, borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
                {erro}
              </div>
            )}

            {sucesso && (
              <div style={{ background: "#064e3b", color: "#d1fae5", padding: 8, borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
                {sucesso}
              </div>
            )}

            <div className="pc-form-grid">
              <div className="pc-form-group pc-form-group-full">
                <label>Visita (Instalação Avulsa)</label>
                <select
                  value={visitaId}
                  onChange={(e) => setVisitaId(e.target.value)}
                  className="pc-input"
                  disabled={carregando}
                >
                  <option value="">Sem visita (preencher cliente e academia manualmente)</option>
                  {visitas.map((v) => {
                    const clienteNome = clientes.find((c) => String(c.id) === String(v.clienteId))?.nome || "Cliente";
                    const academiaNome = academias.find((a) => String(a.id) === String(v.academiaId))?.nome || "Academia";
                    return (
                      <option key={v.id} value={v.id}>
                        {clienteNome} • {academiaNome} • {v.id.slice(0, 6)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="pc-form-group">
                <label>Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="pc-input"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pc-form-group">
                <label>Selecionar cliente</label>
                <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} className="pc-input" disabled={carregando}>
                  <option value="">Selecione</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome || c.razaoSocial || c.id}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pc-form-group">
                <label>Selecionar academia</label>
                <select value={academiaId} onChange={(e) => setAcademiaId(e.target.value)} className="pc-input" disabled={!clienteId}>
                  <option value="">Selecione</option>
                  {academias.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nome || a.codigo || a.id}
                    </option>
                  ))}
                </select>
              </div>

              <label className="pc-form-group pc-form-group-full">
                Observacoes
                <textarea rows={3} className="pc-input" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
              </label>

              <div className="pc-form-group pc-form-group-full" style={{ marginTop: 6 }}>
                <strong>Quantidade de Equipamentos por tipo</strong>
              </div>
              <div className="pc-form-group pc-form-group-full">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                  {TIPOS_EQUIPAMENTO.map((tipo) => (
                    <div
                      key={tipo}
                      style={{ border: "1px solid rgba(148,163,184,0.2)", borderRadius: 8, padding: "8px 10px", background: "rgba(15,23,42,0.35)" }}
                    >
                      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, opacity: 0.8, cursor: "pointer" }}>
                        <div>{tipo}</div>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={quantEquipPorTipo[tipo] || 0}
                          onChange={(e) =>
                            setQuantEquipPorTipo((prev) => ({
                              ...prev,
                              [tipo]: Number(e.target.value) || 0,
                            }))
                          }
                          className="pc-input"
                          style={{ fontWeight: 700 }}
                        />
                        <small style={{ opacity: 0.7 }}>R$ {formatNumero(valoresInstalacaoPorTipo[tipo] || 0)}</small>
                      </label>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontWeight: 600 }}>Total: {totalEquipamentos}</div>
                <div style={{ marginTop: 4, fontWeight: 600 }}>Valor equipamentos: R$ {formatNumero(valorEquipamentos)}</div>
              </div>

              {isEdicao && (
                <div className="pc-form-group pc-form-group-full" style={{ marginTop: 6 }}>
                  <strong>Entrada rápida • Equipamentos da proposta</strong>
                  <p style={{ marginTop: 4, marginBottom: 10, opacity: 0.8, fontSize: 13 }}>
                    Preencha em lote para evitar edição individual por formulário.
                    {" "}Equipamentos vinculados: <b>{maquinasVinculadasProposta.length}</b>
                    {" "}| Selecionados: <b>{totalSelecionadas}</b>
                  </p>

                  {maquinasVinculadasProposta.length > 0 ? (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, opacity: 0.8 }}>Marca (lote)</span>
                          <input
                            className="pc-input"
                            value={loteMarca}
                            onChange={(e) => setLoteMarca(e.target.value)}
                            placeholder="Ex.: Movement"
                          />
                        </label>

                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, opacity: 0.8 }}>Modelo (lote)</span>
                          <input
                            className="pc-input"
                            value={loteModelo}
                            onChange={(e) => setLoteModelo(e.target.value)}
                            placeholder="Ex.: MX-200"
                          />
                        </label>

                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, opacity: 0.8 }}>Checklist (lote)</span>
                          <select
                            className="pc-input"
                            value={loteChecklistId}
                            onChange={(e) => setLoteChecklistId(e.target.value)}
                            disabled={carregandoChecklists}
                          >
                            <option value="">Sem checklist</option>
                            {checklistModelos.map((modelo) => (
                              <option key={modelo.id} value={modelo.id}>
                                {modelo.nome || modelo.codigo || modelo.id}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                        <button type="button" className="pc-btn-secundario" onClick={selecionarTodos}>
                          Selecionar todos
                        </button>
                        <button type="button" className="pc-btn-secundario" onClick={limparSelecao}>
                          Limpar seleção
                        </button>
                        <button type="button" className="pc-btn-secundario" onClick={selecionarPendentes}>
                          Selecionar pendentes
                        </button>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                        <button
                          type="button"
                          className="pc-btn-secundario"
                          onClick={() => handleAplicarLoteDados({ somenteSelecionadas: true })}
                        >
                          Aplicar marca/modelo/checklist nos selecionados
                        </button>
                        <button type="button" className="pc-btn-secundario" onClick={() => handleAplicarLoteDados({ somenteSelecionadas: false })}>
                          Aplicar marca/modelo/checklist em todos
                        </button>
                      </div>

                      <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                        <span style={{ fontSize: 12, opacity: 0.8 }}>
                          Colar números de série (1 por linha, aplicado na ordem da tabela)
                        </span>
                        <textarea
                          rows={4}
                          className="pc-input"
                          value={loteSeriaisTexto}
                          onChange={(e) => setLoteSeriaisTexto(e.target.value)}
                          placeholder={`SERIAL-001\nSERIAL-002\nSERIAL-003`}
                        />
                      </label>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                        <button
                          type="button"
                          className="pc-btn-secundario"
                          onClick={() => handleAplicarLoteSeriais({ somenteSelecionadas: true })}
                        >
                          Aplicar seriais nos selecionados
                        </button>
                        <button type="button" className="pc-btn-secundario" onClick={() => handleAplicarLoteSeriais({ somenteSelecionadas: false })}>
                          Aplicar seriais em todos
                        </button>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                        <button
                          type="button"
                          className="pc-btn-primario"
                          onClick={() => handleSalvarEntradaRapida({ somenteSelecionadas: true })}
                          disabled={salvandoEntradaRapida || totalSelecionadas <= 0}
                        >
                          {salvandoEntradaRapida ? "Salvando lote..." : "Salvar selecionados"}
                        </button>
                        <button
                          type="button"
                          className="pc-btn-primario"
                          onClick={() => handleSalvarEntradaRapida({ somenteSelecionadas: false })}
                          disabled={salvandoEntradaRapida}
                        >
                          {salvandoEntradaRapida ? "Salvando lote..." : "Salvar todos"}
                        </button>
                      </div>

                      <div style={{ overflowX: "auto", border: "1px solid rgba(148,163,184,0.22)", borderRadius: 10 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
                          <thead>
                            <tr style={{ background: "rgba(30,41,59,0.45)" }}>
                              <th style={{ textAlign: "center", padding: "8px 10px", width: 56 }}>
                                <input
                                  type="checkbox"
                                  checked={allSelecionadas}
                                  onChange={(e) => {
                                    if (e.target.checked) selecionarTodos();
                                    else limparSelecao();
                                  }}
                                />
                              </th>
                              <th style={{ textAlign: "left", padding: "8px 10px" }}>Código</th>
                              <th style={{ textAlign: "left", padding: "8px 10px" }}>Nome</th>
                              <th style={{ textAlign: "left", padding: "8px 10px" }}>Marca</th>
                              <th style={{ textAlign: "left", padding: "8px 10px" }}>Modelo</th>
                              <th style={{ textAlign: "left", padding: "8px 10px" }}>Nº Série</th>
                              <th style={{ textAlign: "left", padding: "8px 10px" }}>Checklist instalação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {maquinasVinculadasProposta.map((maq) => {
                              const draft = edicaoMaquinas[maq.id] || {
                                marca: "",
                                modelo: "",
                                numeroSerie: "",
                                checklistInstalacaoId: "",
                                checklistInstalacaoNome: "",
                              };

                              return (
                                <tr key={maq.id} style={{ borderTop: "1px solid rgba(148,163,184,0.16)" }}>
                                  <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                    <input
                                      type="checkbox"
                                      checked={!!selecionadasMaquinas[maq.id]}
                                      onChange={(e) => alternarSelecao(maq.id, e.target.checked)}
                                    />
                                  </td>
                                  <td style={{ padding: "8px 10px", whiteSpace: "nowrap", fontWeight: 700 }}>
                                    {maq.codigo || maq.id.slice(0, 6)}
                                  </td>
                                  <td style={{ padding: "8px 10px", minWidth: 200 }}>{maq.nome || "-"}</td>
                                  <td style={{ padding: "8px 10px" }}>
                                    <input
                                      className="pc-input"
                                      value={draft.marca}
                                      onChange={(e) => atualizarMaquinaEdicao(maq.id, "marca", e.target.value)}
                                    />
                                  </td>
                                  <td style={{ padding: "8px 10px" }}>
                                    <input
                                      className="pc-input"
                                      value={draft.modelo}
                                      onChange={(e) => atualizarMaquinaEdicao(maq.id, "modelo", e.target.value)}
                                    />
                                  </td>
                                  <td style={{ padding: "8px 10px" }}>
                                    <input
                                      className="pc-input"
                                      value={draft.numeroSerie}
                                      onChange={(e) => atualizarMaquinaEdicao(maq.id, "numeroSerie", e.target.value)}
                                    />
                                  </td>
                                  <td style={{ padding: "8px 10px", minWidth: 220 }}>
                                    <select
                                      className="pc-input"
                                      value={draft.checklistInstalacaoId}
                                      onChange={(e) => {
                                        const checklistId = String(e.target.value || "");
                                        atualizarMaquinaEdicao(maq.id, "checklistInstalacaoId", checklistId);
                                        atualizarMaquinaEdicao(
                                          maq.id,
                                          "checklistInstalacaoNome",
                                          checklistId ? (checklistNomeById.get(checklistId) || "") : ""
                                        );
                                      }}
                                      disabled={carregandoChecklists}
                                    >
                                      <option value="">Sem checklist</option>
                                      {checklistModelos.map((modelo) => (
                                        <option key={modelo.id} value={modelo.id}>
                                          {modelo.nome || modelo.codigo || modelo.id}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: 10, borderRadius: 8, background: "rgba(15,23,42,0.35)", opacity: 0.85 }}>
                      Nenhum equipamento vinculado a esta proposta ainda.
                    </div>
                  )}
                </div>
              )}

              <div style={{ gridColumn: "1 / -1", fontWeight: 700, opacity: 0.9, marginTop: 6 }}>
                Endereço e Navegação
              </div>

              <label className="pc-form-group pc-form-group-full">
                Endereço de destino (academia/cliente)
                <input type="text" value={enderecoDestino} onChange={(e) => setEnderecoDestino(e.target.value)} className="pc-input" />
              </label>

              <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  className="pc-btn-secundario"
                  onClick={handleCalcularDistancia}
                  disabled={!enderecoDestino || !enderecoOrigem || calculandoDistancia}
                >
                  {calculandoDistancia ? "Calculando..." : "📍 Calcular distância"}
                </button>

                {enderecoDestino && (
                  <button
                    type="button"
                    className="pc-btn-secundario"
                    onClick={() => {
                      const url = gerarUrlGoogleMaps(enderecoDestino, enderecoOrigem || null);
                      window.open(url, "_blank");
                    }}
                  >
                    🗺️ Abrir no Google Maps
                  </button>
                )}

                <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={idaVolta}
                    onChange={(e) => setIdaVolta(e.target.checked)}
                    style={{ margin: 0, width: 18, height: 18, cursor: "pointer" }}
                  />
                  <span style={{ fontSize: 13, cursor: "pointer" }}>Ida e Volta</span>
                </label>

                {!enderecoOrigem && (
                  <div style={{ fontSize: 12, color: "#f59e0b", padding: 4 }}>
                    ⚠️ Configure o endereço de origem em Opções
                  </div>
                )}
              </div>

              <div style={{ gridColumn: "1 / -1", fontWeight: 700, opacity: 0.9, marginTop: 8 }}>
                Financeiro • Deslocamento
              </div>

              <label className="pc-form-group">
                Km rodados
                <input type="number" min="0" step="0.1" value={kmRodados} onChange={(e) => setKmRodados(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Valor por km (R$)
                <input type="text" readOnly value={formatNumero(valorKmDeslocamento, 2)} className="pc-input" style={{ opacity: 0.7, cursor: "not-allowed" }} />
                <small style={{ opacity: 0.6, marginTop: 2, display: "block" }}>Configurado em Opções / Grupo</small>
              </label>

              <label className="pc-form-group">
                Valor deslocamento (R$)
                <input type="text" value={valorDeslocamento} onChange={(e) => setValorDeslocamento(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Pedagio (R$)
                <input type="number" min="0" step="0.01" value={valorPedagio} onChange={(e) => setValorPedagio(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Desconto (R$)
                <input type="number" min="0" step="0.01" value={valorDesconto} onChange={(e) => setValorDesconto(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Tipo de viagem
                <select value={tipoViagem} onChange={(e) => setTipoViagem(e.target.value)} className="pc-input">
                  <option value="CARRO">Carro</option>
                  <option value="AVIAO">Avião</option>
                </select>
              </label>

              <label className="pc-form-group">
                Quantidade de ajudantes
                <input type="number" min="0" step="1" value={quantidadeAjudantes} onChange={(e) => setQuantidadeAjudantes(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Gastos com Almoço (R$)
                <input type="number" min="0" step="0.01" value={gastosAlmoco} onChange={(e) => setGastosAlmoco(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group">
                Previsão de dias
                <input type="number" min="1" step="1" value={previsaoDias} onChange={(e) => setPrevisaoDias(e.target.value)} className="pc-input" />
              </label>

              <label className="pc-form-group pc-form-group-full">
                Valor total (R$)
                <input type="text" readOnly className="pc-input" value={formatNumero(valorTotal)} />
              </label>
            </div>

            <div className="pc-form-actions">
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleGerarPdf}
                disabled={salvando || carregando || gerandoPdf || enviandoEmail}
              >
                {gerandoPdf ? "Gerando PDF..." : "Gerar PDF"}
              </button>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleEnviarEmail}
                disabled={salvando || carregando || gerandoPdf || enviandoEmail}
              >
                {enviandoEmail ? "Enviando e-mail..." : "Enviar por e-mail"}
              </button>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/financeiro/instalacoes")}
                disabled={salvando || enviandoEmail}
              >
                Voltar
              </button>
              <button type="submit" className="pc-btn-primario" disabled={salvando || carregando || enviandoEmail}>
                {salvando ? "Salvando..." : isEdicao ? "Salvar alterações" : "Salvar proposta"}
              </button>
            </div>
          </form>
        )}
      </section>
    </LayoutDesktop>
  );
}
